import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { FacilityDetailPageSEO } from '@/components/SEOHead';
import RelatedBlogPosts from '@/components/RelatedBlogPosts';
import { 
  MapPin, Clock, CheckCircle, ChevronLeft, 
  Wifi, Car, Coffee, Users, Shield, Star, MessageSquare, Tag, X, Gift, Sparkles, FileText, Phone, Award, Heart, Bell, Ruler, Layers, UserCheck, ShowerHead, ParkingCircle,
  AlertCircle,
} from 'lucide-react';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import { facilityFeatures, sportBranches, surfaceTypes, fieldSizes, capacityOptions } from '@/data/turkeyData';
import { getFacilityTypeLabel } from '@/data/facilityTypes';

const API = `${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/(api)?\/?$/, "")}/api`;

const FacilityDetailPage = () => {
  const { facilityId } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated, login } = useAuth();

  const [facility, setFacility] = useState(null);
  const [slots, setSlots] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedSlots, setSelectedSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFavorite, setIsFavorite] = useState(false);
  const [watchingSlots, setWatchingSlots] = useState({}); // {hour: watcher_id}
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [mainImage, setMainImage] = useState(0);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [reviews, setReviews] = useState([]);
  
  // Campaign code state
  const [campaignCode, setCampaignCode] = useState('');
  const [campaignDiscount, setCampaignDiscount] = useState(null);
  const [validatingCampaign, setValidatingCampaign] = useState(false);
  
  // Terms agreement state
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);

  useEffect(() => {
    fetchFacility();
    fetchReviews();
    if (isAuthenticated) {
      checkIsFavorite();
    }
  }, [facilityId, isAuthenticated]);

  // Update document title when facility data is loaded
  useEffect(() => {
    if (facility) {
      const facilityType = getFacilityTypeLabel(facility.facility_type);
      document.title = `${facility.name} | ${facility.district} ${facilityType} Kirala | TesisKirala.com`;
    }
    return () => {
      document.title = 'TesisKirala.com | Halı Saha, Spor Salonu Kiralama | Online Rezervasyon';
    };
  }, [facility]);

  useEffect(() => {
    if (facility) {
      fetchSlots();
    }
  }, [selectedDate, facility]);

  const fetchFacility = async () => {
    try {
      const response = await axios.get(`${API}/facilities/${facilityId}`);
      setFacility(response.data);
    } catch (error) {
      console.error('Error fetching facility:', error);
      toast.error('Tesis bilgileri yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const fetchReviews = async () => {
    try {
      const response = await axios.get(`${API}/reviews/facility/${facilityId}`);
      setReviews(response.data);
    } catch (error) {
      console.error('Error fetching reviews:', error);
    }
  };

  // Check if facility is in favorites
  const checkIsFavorite = async () => {
    try {
      const response = await axios.get(`${API}/user/favorites/${facilityId}/check`, {
        withCredentials: true
      });
      setIsFavorite(response.data.is_favorite);
    } catch (error) {
      console.log('Error checking favorite status');
    }
  };

  // Toggle favorite
  const toggleFavorite = async () => {
    if (!isAuthenticated) {
      toast.error('Favorilere eklemek için giriş yapmalısınız');
      return;
    }

    try {
      if (isFavorite) {
        await axios.delete(`${API}/user/favorites/${facilityId}`, {
          withCredentials: true
        });
        setIsFavorite(false);
        toast.success('Favorilerden kaldırıldı');
      } else {
        await axios.post(`${API}/user/favorites`, {
          facility_id: facilityId
        }, { withCredentials: true });
        setIsFavorite(true);
        toast.success('Favorilere eklendi');
      }
    } catch (error) {
      toast.error('İşlem başarısız');
    }
  };

  // Watch slot - notify when cancelled
  const watchSlot = async (hour) => {
    if (!isAuthenticated) {
      toast.error('Bu özellik için giriş yapmalısınız');
      return;
    }

    const dateStr = format(selectedDate, 'yyyy-MM-dd');
    const isCurrentlyWatching = watchingSlots[hour];
    
    try {
      const response = await axios.post(`${API}/user/slot-watchers`, {
        facility_id: facilityId,
        date: dateStr,
        hour: hour
      }, { withCredentials: true });
      
      if (response.data.action === 'removed') {
        // Removed from watchlist
        setWatchingSlots(prev => {
          const newState = { ...prev };
          delete newState[hour];
          return newState;
        });
        toast.success('Takip kaldırıldı');
      } else {
        // Added to watchlist
        setWatchingSlots(prev => ({ ...prev, [hour]: response.data.watcher_id }));
        toast.success('İptal olursa e-posta ile bilgilendirileceksiniz');
      }
    } catch (error) {
      console.error('Watcher toggle error:', error);
      toast.error(error.response?.data?.detail || 'İşlem başarısız');
    }
  };

  const fetchSlots = async () => {
    setSlotsLoading(true);
    try {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      const response = await axios.get(`${API}/facilities/${facilityId}/slots?date=${dateStr}`);
      setSlots(response.data);
      setSelectedSlots([]);
    } catch (error) {
      console.error('Error fetching slots:', error);
    } finally {
      setSlotsLoading(false);
    }
  };

  const toggleSlot = (slot) => {
    if (!slot.is_available) return;

    setSelectedSlots(prev => {
      const exists = prev.find(s => s.slot_id === slot.slot_id);
      if (exists) {
        return prev.filter(s => s.slot_id !== slot.slot_id);
      }
      
      // Only allow consecutive slots
      if (prev.length > 0) {
        const lastSlot = prev[prev.length - 1];
        const slotIndex = slots.findIndex(s => s.slot_id === slot.slot_id);
        const lastIndex = slots.findIndex(s => s.slot_id === lastSlot.slot_id);
        
        if (Math.abs(slotIndex - lastIndex) !== 1) {
          toast.error('Sadece ardışık saatler seçilebilir');
          return prev;
        }
      }
      
      return [...prev, slot].sort((a, b) => a.start_time.localeCompare(b.start_time));
    });
  };

  const calculateTotal = () => {
    return selectedSlots.reduce((sum, slot) => sum + slot.price, 0);
  };

  const getFinalTotal = () => {
    const total = calculateTotal();
    if (campaignDiscount) {
      return campaignDiscount.final_amount;
    }
    return total;
  };

  const validateCampaignCode = async () => {
    if (!campaignCode.trim()) {
      toast.error('Lütfen bir kampanya kodu girin');
      return;
    }

    const total = calculateTotal();
    if (total === 0) {
      toast.error('Önce saat seçimi yapın');
      return;
    }

    setValidatingCampaign(true);
    try {
      const response = await axios.post(`${API}/campaigns/validate`, {
        code: campaignCode,
        amount: total
      });
      setCampaignDiscount(response.data);
      toast.success(`🎉 ${response.data.discount_amount.toFixed(2)} ₺ indirim uygulandı!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Geçersiz kampanya kodu');
      setCampaignDiscount(null);
    } finally {
      setValidatingCampaign(false);
    }
  };

  const removeCampaignCode = () => {
    setCampaignCode('');
    setCampaignDiscount(null);
  };

  const handleBooking = async () => {
    if (!isAuthenticated) {
      toast.info('Rezervasyon için giriş yapmanız gerekiyor');
      login();
      return;
    }

    if (selectedSlots.length === 0) {
      toast.error('Lütfen en az bir saat dilimi seçin');
      return;
    }

    if (!termsAccepted) {
      toast.error('Rezervasyon yapmak için kullanıcı sözleşmesini onaylamanız gerekmektedir');
      return;
    }

    setBookingLoading(true);
    try {
      const startTime = selectedSlots[0].start_time;
      const endTime = selectedSlots[selectedSlots.length - 1].end_time;

      const bookingData = {
        facility_id: facilityId,
        date: format(selectedDate, 'yyyy-MM-dd'),
        start_time: startTime,
        end_time: endTime
      };

      // Add campaign code if applied
      if (campaignDiscount) {
        bookingData.campaign_code = campaignCode;
      }

      const response = await axios.post(`${API}/bookings`, bookingData, { withCredentials: true });

      if (!response.data || !response.data.booking_id) {
        throw new Error('Rezervasyon yanıtı alınamadı');
      }

      toast.success('Rezervasyon oluşturuldu!');
      navigate(`/rezervasyon/${response.data.booking_id}`);
    } catch (error) {
      console.error('Booking error:', error);
      toast.error(error.response?.data?.detail || 'Rezervasyon oluşturulamadı');
    } finally {
      setBookingLoading(false);
    }
  };

  // Cancellation Policy
  const cancellationPolicy = facility?.cancellation_policy || "Rezervasyon saatine 24 saat kala iptal edilebilir. Geç iptallerde ücret iadesi yapılmaz.";
  
  // Terms and Conditions Text
  const termsText = `TESİSKİRALA.COM KULLANICI SÖZLEŞMESİ

Tanımlar

"tesiskirala.com ve mobil internet sitesi", Türkiye Cumhuriyeti kanunlarına göre kurulmuş, Avrasya Sportif Faaliyetler Limited Şirketi'ne ait ve şirket merkezi Türkiye'de bulunan dijital platformu ifade eder.

"Hizmet", tesiskirala.com ve mobil internet sitesi üzerinden, Tesisler tarafından sunulan ürün ve hizmetlerin çevrimiçi ortamda listelenmesi, rezervasyonunun yapılması ve gerekli hallerde ödeme alınmasını kapsayan hizmetleri ifade eder.

"Tesis", tesiskirala.com platformu üzerinde rezervasyon için sunulan spor alanı, spor tesisi veya benzeri hizmet sağlayıcılarını (örneğin; halı saha, spor salonu, açık/kapalı spor alanları vb.) ifade eder.

Hizmetin Kapsamı

tesiskirala.com ve mobil internet sitesi; Tesislerin ürün ve hizmetlerini rezervasyon amacıyla ilan edebileceği, kullanıcıların ise bu hizmetler için çevrimiçi rezervasyon yapabileceği bir aracı platformdur.

Platform üzerinden yapılan rezervasyonlar sonucunda kullanıcı, rezervasyon yaptığı Tesis ile doğrudan ve hukuken bağlayıcı bir sözleşme ilişkisine girmiş olur. tesiskirala.com, yalnızca rezervasyonun Tesise iletilmesi ve rezervasyon teyidinin kullanıcıya SMS veya dijital yollarla bildirilmesi hususunda aracı hizmet sağlayıcı sıfatıyla hareket eder.

tesiskirala.com'da yer alan tüm fiyat, kontenjan ve içerik bilgileri Tesisler tarafından sağlanmakta olup, bu bilgilerin güncel ve doğru tutulmasından münhasıran Tesisler sorumludur. tesiskirala.com, makul özen göstermesine rağmen bilgilerin eksiksizliği, doğruluğu veya hatasızlığı konusunda garanti vermez; yazım hataları, teknik arızalar, bakım çalışmaları veya geçici kesintilerden dolayı sorumluluk kabul etmez.

tesiskirala.com, herhangi bir Tesisin sunduğu hizmetlerin kalitesi, seviyesi veya uygunluğu konusunda açık ya da zımni bir garanti vermez ve bu yönde bir tavsiye niteliği taşımaz.

Kullanım Koşulları

Platform yalnızca kişisel ve ticari olmayan amaçlarla kullanılabilir. Platformda yer alan içeriklerin, yazılımların, fiyatların veya rezervasyonların ticari amaçla kopyalanması, çoğaltılması, yeniden satılması, yayınlanması veya izinsiz kullanılması yasaktır.

Fiyat Politikası

tesiskirala.com üzerinde yer alan tüm fiyatlar Tesisler tarafından belirlenir. Bazı fiyatlar özel koşullara (iptal edilemez, iadesiz vb.) tabi olabilir. Kullanıcılar, rezervasyon öncesinde ilgili Tesisin fiyat ve koşullarını dikkatle incelemekle yükümlüdür.

Ön Ödeme, İptal ve Kullanılmayan Rezervasyonlar

Kullanıcı, rezervasyon yapmasıyla birlikte ilgili Tesisin iptal, iade, ön ödeme ve rezervasyonun kullanılmaması durumlarına ilişkin koşullarını kabul etmiş sayılır. Tesisler, rezervasyon sırasında belirli bir tutarı kapora veya ön ödeme olarak tahsil edebilir.

İptal edilemeyen veya ücret iadesi yapılmayan rezervasyonlarda, kapora veya ön ödeme iadesi yapılmayabilir. Ödeme zamanında ve eksiksiz yapılmadığı takdirde rezervasyon iptal edilebilir.

Geç varış, rezervasyonun kullanılmaması veya Tesis tarafından uygulanan cezai şartlardan dolayı tesiskirala.com sorumluluk kabul etmez.

tesiskirala.com üzerinden yapılan bir rezervasyon, kullanıcıya rezervasyon onay SMS'i ulaşmadıkça kesinleşmiş sayılmaz. Uygunluk veya güncelleme durumlarında rezervasyon reddedilebilir; bu durumda kullanıcının yalnızca ödediği tutarın iadesi talep hakkı vardır.

Fikri Mülkiyet Hakları

tesiskirala.com ve mobil internet sitesinde yer alan tüm içerik, yazılım, tasarım, marka, logo ve altyapı unsurlarının fikri mülkiyet hakları Avrasya Sportif Faaliyetler Limited Şirketi'ne aittir.

İzinsiz kopyalama, çoğaltma, dağıtma, bağlantı verme veya ticari kullanım fikri mülkiyet ihlali teşkil eder.

Hesap Güvenliği

Bazı hizmetlerden faydalanabilmek için kullanıcı hesabı oluşturulması gerekebilir. Hesap güvenliği ve hesap üzerinden yapılan tüm işlemler kullanıcının sorumluluğundadır.

Ödeme Hizmetleri

Kullanıcı, ödeme işlemlerinin gerçekleştirilmesi ve ödeme güvenliğinin sağlanması amacıyla, ödeme bilgilerinin ilgili ödeme kuruluşlarına aktarılmasına ve bu kuruluşların kendi gizlilik politikaları kapsamında işlenmesine açık rıza göstermektedir.

Uygulanacak Hukuk ve Yetki

İşbu Kullanıcı Sözleşmesi Türkiye Cumhuriyeti kanunlarına tabidir. Sözleşmeden doğabilecek uyuşmazlıklarda İstanbul Anadolu Mahkemeleri ve İcra Daireleri yetkilidir.

Yürürlük

İşbu Sözleşme, kullanıcının platforma kayıt olmasıyla birlikte süresiz olarak yürürlüğe girer.

Fesih

Taraflar, işbu Sözleşme'yi diledikleri zaman feshedebilir. Fesih, tarafların doğmuş hak ve alacaklarını ortadan kaldırmaz.`;

  const featureIcons = {
    'Otopark': Car,
    'Wifi': Wifi,
    'Kafeterya': Coffee,
    'Soyunma Odası': Users,
    'Güvenlik': Shield,
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!facility) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-slate-900 mb-2">Tesis Bulunamadı</h2>
          <Button onClick={() => navigate('/tesisler')}>
            Tesislere Dön
          </Button>
        </div>
      </div>
    );
  }

  const images = facility.images?.length > 0 ? facility.images : [
    facility.facility_type === 'hali_saha' 
      ? 'https://images.pexels.com/photos/47730/the-ball-stadion-football-the-pitch-47730.jpeg'
      : 'https://images.unsplash.com/photo-1546519638-68e109498ffc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwxfHxpbmRvb3IlMjBiYXNrZXRiYWxsJTIwY291cnR8ZW58MHx8fHwxNzY2NDEyMDIzfDA&ixlib=rb-4.1.0&q=85'
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* SEO Head */}
      <FacilityDetailPageSEO facility={facility} />
      
      {/* Back Button */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Button 
            variant="ghost" 
            onClick={() => navigate(-1)}
            className="text-slate-600"
            data-testid="back-btn"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Geri
          </Button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Gallery */}
            <div className="space-y-4" data-testid="facility-gallery">
              <div className="gallery-main bg-slate-200">
                <img 
                  src={images[mainImage]} 
                  alt={facility.name}
                  className="w-full h-full object-cover"
                />
              </div>
              {images.length > 1 && (
                <div className="grid grid-cols-4 gap-2">
                  {images.map((img, idx) => (
                    <div 
                      key={idx}
                      className={`gallery-thumb ${mainImage === idx ? 'active' : ''}`}
                      onClick={() => setMainImage(idx)}
                    >
                      <img src={img} alt={`${facility.name} ${idx + 1}`} className="w-full h-full object-cover" />
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Facility Info */}
            <Card data-testid="facility-info">
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Badge variant="secondary">
                        {getFacilityTypeLabel(facility.facility_type)}
                      </Badge>
                      {facility.is_partner && (
                        <Badge className="bg-amber-500 text-white flex items-center gap-1">
                          <Award className="w-3 h-3" />
                          Partner
                        </Badge>
                      )}
                    </div>
                    <h1 className="text-2xl md:text-3xl font-bold text-slate-900">
                      {facility.name}
                    </h1>
                  </div>
                  <div className="flex items-center gap-3">
                    {/* Favorite Button */}
                    <button
                      onClick={toggleFavorite}
                      className={`p-2 rounded-full transition-all ${
                        isFavorite 
                          ? 'bg-red-100 text-red-500 hover:bg-red-200' 
                          : 'bg-slate-100 text-slate-400 hover:bg-slate-200 hover:text-red-400'
                      }`}
                      title={isFavorite ? 'Favorilerden kaldır' : 'Favorilere ekle'}
                    >
                      <Heart className={`w-6 h-6 ${isFavorite ? 'fill-red-500' : ''}`} />
                    </button>
                    <div className="price-tag text-lg">
                      {facility.price_per_hour} ₺/saat
                    </div>
                  </div>
                </div>

                <div className="flex items-center text-slate-600 mb-6">
                  <MapPin className="w-5 h-5 mr-2 text-emerald-600" />
                  {facility.address}, {facility.district}, {facility.city}
                </div>

                {/* Detailed Facility Info */}
                {(facility.field_size || facility.surface_type || facility.capacity) && (
                  <div className="mb-6 p-4 bg-slate-50 rounded-xl">
                    <h3 className="font-bold text-slate-900 mb-3 flex items-center gap-2">
                      <Ruler className="w-4 h-4" />
                      Saha Bilgileri
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {facility.field_size && (
                        <div className="text-center p-3 bg-white rounded-lg">
                          <Ruler className="w-5 h-5 mx-auto mb-1 text-emerald-600" />
                          <p className="text-xs text-slate-500">Boyut</p>
                          <p className="font-semibold text-slate-800">{facility.field_size}</p>
                        </div>
                      )}
                      {facility.surface_type && (
                        <div className="text-center p-3 bg-white rounded-lg">
                          <Layers className="w-5 h-5 mx-auto mb-1 text-emerald-600" />
                          <p className="text-xs text-slate-500">Zemin</p>
                          <p className="font-semibold text-slate-800">
                            {surfaceTypes.find(s => s.id === facility.surface_type)?.label || facility.surface_type}
                          </p>
                        </div>
                      )}
                      {facility.capacity && (
                        <div className="text-center p-3 bg-white rounded-lg">
                          <Users className="w-5 h-5 mx-auto mb-1 text-emerald-600" />
                          <p className="text-xs text-slate-500">Kapasite</p>
                          <p className="font-semibold text-slate-800">{facility.capacity} Kişi</p>
                        </div>
                      )}
                      {facility.indoor !== undefined && (
                        <div className="text-center p-3 bg-white rounded-lg">
                          <Shield className="w-5 h-5 mx-auto mb-1 text-emerald-600" />
                          <p className="text-xs text-slate-500">Alan Tipi</p>
                          <p className="font-semibold text-slate-800">{facility.indoor ? 'Kapalı' : 'Açık'}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Quick Amenities */}
                <div className="flex flex-wrap gap-3 mb-6">
                  {facility.has_changing_room && (
                    <span className="inline-flex items-center gap-1 px-3 py-1 bg-emerald-50 text-emerald-700 rounded-full text-sm">
                      🚪 Soyunma Odası
                    </span>
                  )}
                  {facility.has_shower && (
                    <span className="inline-flex items-center gap-1 px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm">
                      🚿 Duş
                    </span>
                  )}
                  {facility.has_parking && (
                    <span className="inline-flex items-center gap-1 px-3 py-1 bg-purple-50 text-purple-700 rounded-full text-sm">
                      🅿️ Otopark {facility.parking_type === 'free' ? '(Ücretsiz)' : facility.parking_type === 'paid' ? '(Ücretli)' : ''}
                    </span>
                  )}
                  {facility.has_cafeteria && (
                    <span className="inline-flex items-center gap-1 px-3 py-1 bg-amber-50 text-amber-700 rounded-full text-sm">
                      ☕ Kafeterya
                    </span>
                  )}
                  {facility.has_equipment_rental && (
                    <span className="inline-flex items-center gap-1 px-3 py-1 bg-rose-50 text-rose-700 rounded-full text-sm">
                      ⚽ Ekipman Kiralama
                    </span>
                  )}
                  {facility.has_floodlights && (
                    <span className="inline-flex items-center gap-1 px-3 py-1 bg-yellow-50 text-yellow-700 rounded-full text-sm">
                      💡 Aydınlatma
                    </span>
                  )}
                </div>

                {/* Branches */}
                {facility.branches?.length > 0 && (
                  <div className="mb-6">
                    <h3 className="font-bold text-slate-900 mb-3">Spor Dalları</h3>
                    <div className="flex flex-wrap gap-2">
                      {facility.branches.map((branchId, idx) => {
                        const branch = sportBranches.find(b => b.id === branchId);
                        return (
                          <span key={idx} className="inline-flex items-center px-3 py-1 rounded-full bg-blue-50 text-blue-700 text-sm">
                            {branch ? `${branch.icon} ${branch.label}` : branchId}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Features */}
                {facility.features?.length > 0 && (
                  <div className="mb-6">
                    <h3 className="font-bold text-slate-900 mb-3">Özellikler</h3>
                    <div className="flex flex-wrap gap-2">
                      {facility.features.map((featureId, idx) => {
                        const feature = facilityFeatures.find(f => f.id === featureId);
                        const Icon = featureIcons[featureId] || CheckCircle;
                        return (
                          <span key={idx} className="feature-badge">
                            {feature ? (
                              <span>{feature.icon} {feature.label}</span>
                            ) : (
                              <>
                                <Icon className="w-4 h-4" />
                                {featureId}
                              </>
                            )}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Description */}
                {facility.description && (
                  <div>
                    <h3 className="font-bold text-slate-900 mb-3">Açıklama</h3>
                    <p className="text-slate-600 whitespace-pre-wrap">{facility.description}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Map - Always show using address */}
            <Card data-testid="facility-map">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MapPin className="w-5 h-5" />
                  Konum
                </CardTitle>
                </CardHeader>
                <CardContent>
                  {/* Google Maps Embed - Using place search */}
                  <div className="rounded-xl overflow-hidden border border-slate-200">
                    <iframe
                      title={`${facility.name} Konum`}
                      width="100%"
                      height="300"
                      style={{ border: 0 }}
                      loading="lazy"
                      allowFullScreen
                      referrerPolicy="no-referrer-when-downgrade"
                      src={`https://maps.google.com/maps?q=${encodeURIComponent(facility.address + ', ' + facility.district + ', ' + facility.city)}&t=&z=15&ie=UTF8&iwloc=&output=embed`}
                    />
                  </div>
                  
                  {/* Address and Link */}
                  <div className="mt-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                    <p className="text-slate-600 text-sm">
                      📍 {facility.address}, {facility.district}, {facility.city}
                    </p>
                    {facility.google_maps_link && (
                      <a 
                        href={facility.google_maps_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 bg-emerald-700 hover:bg-emerald-800 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                      >
                        <MapPin className="w-4 h-4" />
                        Yol Tarifi Al
                      </a>
                    )}
                  </div>
                </CardContent>
              </Card>

            {/* Reviews Section */}
            <Card data-testid="facility-reviews">
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <MessageSquare className="w-5 h-5" />
                    Değerlendirmeler
                  </span>
                  {facility.average_rating && (
                    <div className="flex items-center gap-1 text-yellow-500">
                      <Star className="w-5 h-5 fill-yellow-400" />
                      <span className="text-lg font-bold">{facility.average_rating}</span>
                      <span className="text-sm text-slate-500">({facility.review_count || 0} yorum)</span>
                    </div>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {reviews.length > 0 ? (
                  <div className="space-y-4">
                    {reviews.map((review) => (
                      <div key={review.review_id} className="border-b border-slate-100 pb-4 last:border-0">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium text-slate-900">{review.user_name}</span>
                          <div className="flex items-center gap-1">
                            {[1, 2, 3, 4, 5].map((star) => (
                              <Star 
                                key={star}
                                className={`w-4 h-4 ${
                                  star <= review.rating 
                                    ? 'text-yellow-400 fill-yellow-400' 
                                    : 'text-slate-200'
                                }`}
                              />
                            ))}
                          </div>
                        </div>
                        <p className="text-slate-600 text-sm">{review.comment}</p>
                        <p className="text-xs text-slate-400 mt-2">
                          {format(new Date(review.created_at), 'd MMMM yyyy', { locale: tr })}
                        </p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-slate-500">
                    <MessageSquare className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                    <p>Henüz değerlendirme yapılmamış</p>
                    <p className="text-sm">İlk değerlendirmeyi siz yapın!</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Related Blog Posts */}
            <RelatedBlogPosts city={facility.city} facility_type={facility.facility_type} />
          </div>

          {/* Booking Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-24 space-y-6">
              
              {/* Non-Partner Contact Card */}
              {!facility.is_partner && (
                <Card className="border-amber-200 bg-amber-50" data-testid="non-partner-card">
                  <CardContent className="p-6">
                    <div className="text-center">
                      <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Phone className="w-8 h-8 text-amber-600" />
                      </div>
                      <h3 className="text-lg font-bold text-amber-900 mb-2">
                        Rezervasyon İçin Arayın
                      </h3>
                      <p className="text-sm text-amber-700 mb-4">
                        Bu tesis henüz online rezervasyon sistemine katılmadı. Rezervasyon için doğrudan iletişime geçebilirsiniz.
                      </p>
                      {facility.phone ? (
                        <a 
                          href={`tel:${facility.phone}`}
                          className="inline-flex items-center gap-2 bg-amber-600 hover:bg-amber-700 text-white px-6 py-3 rounded-xl font-medium transition-colors text-lg"
                        >
                          <Phone className="w-5 h-5" />
                          {facility.phone}
                        </a>
                      ) : (
                        <p className="text-amber-600 text-sm">Telefon numarası henüz eklenmedi</p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Partner Booking UI - Only show for partners */}
              {facility.is_partner && (
                <>
                  {/* Date Picker */}
                  <Card data-testid="date-picker-card">
                    <CardHeader>
                      <CardTitle>Tarih Seçin</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <Calendar
                        mode="single"
                        selected={selectedDate}
                        onSelect={(date) => date && setSelectedDate(date)}
                        disabled={(date) => date < new Date(new Date().setHours(0,0,0,0))}
                        locale={tr}
                        className="rounded-md border"
                      />
                    </CardContent>
                  </Card>

                  {/* Time Slots */}
                  <Card data-testid="slots-card">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Clock className="w-5 h-5" />
                        Müsait Saatler
                      </CardTitle>
                      <p className="text-sm text-slate-600">
                        {format(selectedDate, 'd MMMM yyyy, EEEE', { locale: tr })}
                      </p>
                    </CardHeader>
                    <CardContent>
                      {slotsLoading ? (
                        <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                          {[1,2,3,4,5,6,7,8].map(i => (
                            <div key={i} className="h-12 bg-slate-100 rounded-lg animate-pulse" />
                          ))}
                        </div>
                      ) : (
                        <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                          {slots.map((slot) => {
                            const hour = parseInt(slot.start_time.split(':')[0]);
                            const isWatching = watchingSlots[hour];
                            
                            return (
                              <button
                                key={slot.slot_id}
                                onClick={() => {
                                  if (slot.is_available) {
                                    toggleSlot(slot);
                                  } else if (!isWatching) {
                                    watchSlot(hour);
                                  }
                                }}
                                className={`
                                  relative w-full h-12 rounded-lg text-sm font-medium transition-all
                                  flex items-center justify-center border
                                  ${!slot.is_available 
                                    ? 'bg-slate-50 border-slate-200 text-slate-400 cursor-pointer hover:border-amber-300 hover:bg-amber-50 hover:text-amber-600 group' 
                                    : selectedSlots.find(s => s.slot_id === slot.slot_id) 
                                      ? 'bg-emerald-600 border-emerald-600 text-white shadow-md transform scale-105 z-10' 
                                      : 'bg-white border-slate-200 text-slate-700 hover:border-emerald-500 hover:text-emerald-600 hover:shadow-sm'}
                                `}
                                data-testid={`slot-${slot.start_time}`}
                                title={!slot.is_available ? (isWatching ? 'Takip ediliyor' : 'İptal olursa bildir') : 'Seç'}
                              >
                                {slot.start_time}
                                
                                {/* Bell Icon for unavailable slots */}
                                {!slot.is_available && (
                                  <div className="absolute top-1 right-1">
                                     {isWatching ? (
                                       <Bell className="w-2.5 h-2.5 text-emerald-500 fill-emerald-500" />
                                     ) : (
                                       <Bell className="w-2.5 h-2.5 text-slate-300 group-hover:text-amber-600 transition-colors" />
                                     )}
                                  </div>
                                )}
                              </button>
                            );
                          })}
                        </div>
                      )}

                      <div className="flex flex-wrap items-center gap-4 mt-4 text-xs text-slate-600">
                        <div className="flex items-center gap-1">
                          <div className="w-3 h-3 rounded bg-white border-2 border-emerald-200" />
                          Müsait
                        </div>
                        <div className="flex items-center gap-1">
                          <div className="w-3 h-3 rounded bg-slate-200" />
                          Dolu
                        </div>
                        <div className="flex items-center gap-1">
                          <div className="w-3 h-3 rounded bg-emerald-100 border-2 border-emerald-500" />
                          Seçili
                        </div>
                        <div className="flex items-center gap-1">
                          <Bell className="w-3 h-3 text-amber-600" />
                          İptal olursa bildir
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Booking Summary */}
                  {selectedSlots.length > 0 && (
                    <Card className="border-emerald-200 bg-emerald-50" data-testid="booking-summary">
                      <CardHeader>
                        <CardTitle>Rezervasyon Özeti</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Tarih</span>
                      <span className="font-medium">{format(selectedDate, 'd MMMM yyyy', { locale: tr })}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Saat</span>
                      <span className="font-medium">
                        {selectedSlots[0].start_time} - {selectedSlots[selectedSlots.length - 1].end_time}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Süre</span>
                      <span className="font-medium">{selectedSlots.length} saat</span>
                    </div>

                    {/* Campaign Code Input */}
                    <div className="border-t border-emerald-200 pt-3">
                      <label className="text-sm text-slate-600 flex items-center gap-1 mb-2">
                        <Tag className="w-4 h-4" />
                        Kampanya Kodu
                      </label>
                      {!campaignDiscount ? (
                        <div className="flex gap-2">
                          <Input
                            value={campaignCode}
                            onChange={(e) => setCampaignCode(e.target.value.toUpperCase())}
                            placeholder="KODUNUZ"
                            className="font-mono uppercase bg-white"
                          />
                          <Button
                            type="button"
                            variant="outline"
                            onClick={validateCampaignCode}
                            disabled={validatingCampaign}
                            className="shrink-0"
                          >
                            {validatingCampaign ? '...' : 'Uygula'}
                          </Button>
                        </div>
                      ) : (
                        <div className="bg-green-100 border border-green-300 rounded-lg p-3">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <CheckCircle className="w-5 h-5 text-green-600" />
                              <code className="font-bold text-green-700">{campaignDiscount.code}</code>
                            </div>
                            <button 
                              onClick={removeCampaignCode}
                              className="text-slate-400 hover:text-slate-600"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                          <p className="text-sm text-green-600 mt-1">
                            {campaignDiscount.discount_type === 'percentage' 
                              ? `%${campaignDiscount.discount_value} indirim`
                              : `${campaignDiscount.discount_value} ₺ indirim`
                            }
                          </p>
                        </div>
                      )}
                    </div>

                    <hr className="border-emerald-200" />
                    
                    {/* Price Summary */}
                    {campaignDiscount ? (
                      <>
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-600">Ara Toplam</span>
                          <span className="line-through text-slate-400">{calculateTotal()} ₺</span>
                        </div>
                        <div className="flex justify-between text-sm text-green-600">
                          <span>İndirim</span>
                          <span>-{campaignDiscount.discount_amount.toFixed(2)} ₺</span>
                        </div>
                        <div className="flex justify-between font-bold text-lg">
                          <span>Toplam</span>
                          <span className="text-emerald-700">{campaignDiscount.final_amount.toFixed(2)} ₺</span>
                        </div>
                      </>
                    ) : (
                      <div className="flex justify-between font-bold text-lg">
                        <span>Toplam</span>
                        <span className="text-emerald-700">{calculateTotal()} ₺</span>
                      </div>
                    )}

                    {/* Cancellation Policy */}
                    <div className="border-t border-emerald-200 pt-3">
                      <div className="bg-amber-50 p-3 rounded-lg border border-amber-100 mb-3">
                        <h4 className="text-sm font-semibold text-amber-800 mb-1 flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" />
                          İptal Koşulları
                        </h4>
                        <p className="text-xs text-amber-700 leading-relaxed">
                          {cancellationPolicy}
                        </p>
                      </div>
                    </div>

                    {/* Terms Agreement Checkbox */}
                    <div className="border-t border-emerald-200 pt-3">
                      <div className="flex items-start gap-3">
                        <Checkbox
                          id="terms"
                          data-testid="terms-checkbox"
                          checked={termsAccepted}
                          onCheckedChange={(checked) => setTermsAccepted(checked === true)}
                          className="mt-0.5"
                        />
                        <label htmlFor="terms" className="text-sm text-slate-600 leading-tight">
                          <span className="block mb-1">
                            Yukarıdaki iptal koşullarını okudum.
                          </span>
                          <button
                            type="button"
                            onClick={() => setShowTermsModal(true)}
                            className="text-emerald-700 hover:text-emerald-800 font-medium underline"
                          >
                            Kullanıcı Sözleşmesi
                          </button>
                          &apos;ni okudum, anladım ve onaylıyorum.
                        </label>
                      </div>
                    </div>

                    <Button 
                      onClick={handleBooking}
                      disabled={bookingLoading || !termsAccepted}
                      className="w-full bg-emerald-700 hover:bg-emerald-800 h-12 text-base rounded-xl disabled:opacity-50"
                      data-testid="book-now-btn"
                    >
                      {bookingLoading ? 'İşleniyor...' : 'Rezervasyon Oluştur'}
                    </Button>

                    {!isAuthenticated && (
                      <p className="text-xs text-center text-slate-600">
                        Rezervasyon için giriş yapmanız gerekecek
                      </p>
                    )}
                  </CardContent>
                </Card>
              )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Terms and Conditions Modal */}
      <Dialog open={showTermsModal} onOpenChange={setShowTermsModal}>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-emerald-600" />
              Kullanıcı Sözleşmesi
            </DialogTitle>
          </DialogHeader>
          <ScrollArea className="h-[50vh] pr-4">
            <div className="prose prose-sm prose-slate">
              <pre className="whitespace-pre-wrap font-sans text-sm text-slate-600 leading-relaxed">
                {termsText}
              </pre>
            </div>
          </ScrollArea>
          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => setShowTermsModal(false)}>
              Kapat
            </Button>
            <Button 
              onClick={() => {
                setTermsAccepted(true);
                setShowTermsModal(false);
              }}
              className="bg-emerald-700 hover:bg-emerald-800"
            >
              Okudum ve Onaylıyorum
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FacilityDetailPage;
